import { Link } from 'react-router-dom';
import {
  WHATSAPP_NUMBER,
  WHATSAPP_1,
  WHATSAPP_1_DISPLAY,
  WHATSAPP_2,
  WHATSAPP_2_DISPLAY,
  STORE_EMAIL,
  STORE_ADDRESS_LINE1,
  STORE_ADDRESS_LINE2,
  STORE_MAPS_URL,
  STORE_HOURS_WEEKDAY,
  STORE_HOURS_SATURDAY,
  STORE_HOURS_SUNDAY,
} from '../constants/contact';
import { WhatsAppIcon, EmailIcon, PinIcon, ClockIcon, SwapIcon } from '../components/ui/ContactIcons';

function ArrowIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
    </svg>
  );
}

function SectionCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-2xl border border-champagne bg-white shadow-sm p-6 ${className}`}>
      {children}
    </div>
  );
}

function CardIcon({ children }: { children: React.ReactNode }) {
  return (
    <span className="w-9 h-9 rounded-full bg-chocolate/10 flex items-center justify-center text-chocolate shrink-0">
      {children}
    </span>
  );
}

export function AboutPage() {
  return (
    <div className="max-w-6xl mx-auto px-4 py-10 md:py-14">
      <section className="max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-chocolate-dark mb-4">
          Sobre a Baby Chic
        </p>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-semibold text-text-dark leading-[1.1] tracking-tight mb-4">
          Vestimos criança <span className="font-display italic font-medium text-chocolate">como</span> criança.
        </h1>
        <p className="text-base md:text-lg text-text-medium leading-relaxed">
          A Baby Chic é uma loja de moda infantil que leva roupas, calçados e acessórios para
          crianças de todo o Brasil. Enviamos para qualquer endereço do país, e também temos uma
          loja física em Fortaleza para quem prefere ver e experimentar as peças pessoalmente.
        </p>
        <div className="w-11 h-0.5 bg-chocolate mt-6" />
      </section>

      <div className="grid sm:grid-cols-2 gap-5 mt-10">
        <a
          href={STORE_MAPS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group rounded-2xl border border-champagne bg-white shadow-sm p-6 hover:border-champagne-dark transition-colors"
        >
          <div className="flex items-center gap-3 mb-3">
            <CardIcon>
              <PinIcon className="w-4 h-4" />
            </CardIcon>
            <h2 className="font-display font-semibold text-text-dark">Nossa loja física</h2>
          </div>
          <p className="text-sm text-text-medium leading-relaxed">
            {STORE_ADDRESS_LINE1}
            <br />
            {STORE_ADDRESS_LINE2}
          </p>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-chocolate mt-3 group-hover:text-chocolate-dark transition-colors">
            Ver no mapa
            <ArrowIcon />
          </span>
        </a>

        <SectionCard>
          <div className="flex items-center gap-3 mb-3">
            <CardIcon>
              <ClockIcon className="w-4 h-4" />
            </CardIcon>
            <h2 className="font-display font-semibold text-text-dark">Horário de atendimento</h2>
          </div>
          <ul className="text-sm text-text-medium leading-relaxed space-y-1">
            <li>{STORE_HOURS_WEEKDAY}</li>
            <li>{STORE_HOURS_SATURDAY}</li>
            <li>{STORE_HOURS_SUNDAY}</li>
          </ul>
        </SectionCard>

        <SectionCard>
          <h2 className="font-display font-semibold text-text-dark mb-3">O que vendemos</h2>
          <p className="text-sm text-text-medium leading-relaxed">
            Roupas, acessórios e calçados do 1 aos 14 anos, para meninas e meninos.
          </p>
        </SectionCard>

        <SectionCard>
          <h2 className="font-display font-semibold text-text-dark mb-3">Contato</h2>
          <ul className="flex flex-col gap-2.5">
            <li>
              <a
                href={`https://wa.me/${WHATSAPP_1}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-text-medium hover:text-chocolate transition-colors"
              >
                <span className="text-success"><WhatsAppIcon className="w-4 h-4" /></span>
                {WHATSAPP_1_DISPLAY}
              </a>
            </li>
            <li>
              <a
                href={`https://wa.me/${WHATSAPP_2}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-text-medium hover:text-chocolate transition-colors"
              >
                <span className="text-success"><WhatsAppIcon className="w-4 h-4" /></span>
                {WHATSAPP_2_DISPLAY}
              </a>
            </li>
            <li>
              <a
                href={`mailto:${STORE_EMAIL}`}
                className="flex items-center gap-2 text-sm text-text-medium hover:text-chocolate transition-colors"
              >
                <EmailIcon className="w-4 h-4" />
                {STORE_EMAIL}
              </a>
            </li>
          </ul>
        </SectionCard>
      </div>

      <SectionCard className="mt-5 bg-champagne-light">
        <h2 className="font-display font-semibold text-text-dark mb-2">Frete e entrega</h2>
        <p className="text-sm text-text-medium leading-relaxed max-w-2xl">
          Enviamos para todo o Brasil. O valor e o prazo variam de acordo com a sua região, então
          combinamos o frete diretamente pelo WhatsApp depois que você finaliza o pedido.
        </p>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-chocolate hover:text-chocolate-dark transition-colors mt-3"
        >
          Combinar frete no WhatsApp
          <ArrowIcon />
        </a>
      </SectionCard>

      <div id="trocas-e-devolucoes" className="scroll-mt-24 rounded-2xl border-2 border-chocolate/25 bg-gradient-to-br from-champagne-light to-white shadow-sm p-6 mt-5">
        <div className="flex items-center gap-3 mb-3">
          <CardIcon>
            <SwapIcon className="w-4 h-4" />
          </CardIcon>
          <h2 className="font-display font-bold text-lg text-text-dark">Política de troca</h2>
        </div>
        <ol className="text-sm text-text-medium leading-relaxed space-y-2 list-decimal list-inside max-w-2xl">
          <li>As peças devem estar sem sinais de uso, lavagem ou alteração, com etiqueta e lacre originais intactos.</li>
          <li>Não realizamos trocas de peças promocionais ou adquiridas em liquidação.</li>
          <li>Não realizamos trocas de peças de coleções sazonais.</li>
          <li>Prazo para a troca: 7 dias corridos.</li>
          <li>A troca da peça pode ser realizada uma vez.</li>
          <li>Não trocamos peças íntimas.</li>
          <li>A troca será permitida apenas por outro modelo da mesma coleção ou por tamanho diferente da mesma peça, conforme disponibilidade em estoque.</li>
        </ol>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3 mt-12">
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-chocolate hover:bg-chocolate-dark text-white font-semibold px-6 py-3 rounded-full transition-colors shadow-sm"
        >
          <WhatsAppIcon className="w-4 h-4" />
          Falar no WhatsApp
        </a>
        <Link
          to="/catalogo"
          className="inline-flex items-center gap-2 text-sm font-medium text-text-medium border border-champagne-dark hover:border-chocolate hover:text-chocolate px-6 py-3 rounded-full transition-colors"
        >
          Ver catálogo
        </Link>
      </div>
    </div>
  );
}
